/**
 * Compliance, Fraud & Risk Report Page
 * Monitors compliance, fraud, and risk aspects
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  Collapse,
  Chip,
  Table,
  TableBody,
  TableHead,
  Alert,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FileDownload as FileDownloadIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner } from '../../components/common';
import { ComplianceRiskService } from '../../services/reports/ComplianceRiskService';
import { formatDateTime } from '../../utils/dateUtils';
import type { ComplianceRiskData, ReportFilters } from '../../types/reports';
import {
  Card,
  CardContent,
  TableCell,
  TableHeaderCell,
  TableRow,
  Select,
  PrimaryButton,
  SecondaryButton,
} from '../../components/ui';

const ComplianceRiskPage: React.FC = () => {
  const theme = useTheme();
  const [data, setData] = useState<ComplianceRiskData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<ReportFilters>({
    dateRange: 'Last 30 Days',
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await ComplianceRiskService.getComplianceRiskData(filters);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch compliance risk data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'High':
        return theme.palette.error.main;
      case 'Medium':
        return theme.palette.warning.main;
      case 'Low':
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'At Risk':
        return theme.palette.warning.main;
      case 'Expired':
        return theme.palette.error.main;
      case 'Warning':
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const handleExportReport = () => {
    // TODO: Implement export functionality
    console.log('Export report');
  };

  if (isLoading && !data) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No data available.
        </Typography>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing(2) }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(0.5),
              }}
            >
              Compliance, Fraud & Risk Report
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
            <SecondaryButton
              size="small"
              endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              Show Filters
            </SecondaryButton>
            <PrimaryButton
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportReport}
            >
              Export Report
            </PrimaryButton>
          </Box>
        </Box>

        {/* Filters */}
        <Collapse in={filtersOpen}>
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              marginBottom: theme.spacing(3),
            }}
          >
            <Box
              sx={{
                padding: theme.spacing(2),
                display: 'flex',
                gap: theme.spacing(2),
                flexWrap: 'wrap',
              }}
            >
              <Select
                size="small"
                label="Date Range"
                value={filters.dateRange || 'Last 30 Days'}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as string })}
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
                <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
                <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
                <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
                <MenuItem value="Custom">Custom</MenuItem>
              </Select>

              <Select
                size="small"
                label="Region"
                value="All Regions"
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="All Regions">All Regions</MenuItem>
                <MenuItem value="Michigan">Michigan</MenuItem>
                <MenuItem value="National">National</MenuItem>
              </Select>

              <Select
                size="small"
                label="Risk Level"
                value="All"
                formControlProps={{ sx: { minWidth: 150 } }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </Select>
            </Box>
          </Card>
        </Collapse>
      </Box>

      {/* PI Compliance Section */}
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
            fontSize: '1rem',
          }}
        >
          PI Compliance
        </Typography>
        <Grid container spacing={2} sx={{ marginBottom: theme.spacing(3) }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                  }}
                >
                  Total PIs
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  {data.piCompliance.totalPIs.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                  }}
                >
                  Verified
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.success.main,
                  }}
                >
                  {data.piCompliance.verified.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                  }}
                >
                  Pending
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.warning.main,
                  }}
                >
                  {data.piCompliance.pending.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                  }}
                >
                  Expired/At Risk
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.warning.main,
                  }}
                >
                  {data.piCompliance.expiredAtRisk.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                  }}
                >
                  Failed
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.error.main,
                  }}
                >
                  {data.piCompliance.failed.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
                minHeight: 400,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    marginBottom: theme.spacing(2),
                    fontSize: '1rem',
                  }}
                >
                  License Status Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.licenseStatusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ status, percent }: any) => `${status}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {data.licenseStatusDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.customColors.tableBorder}`,
                        borderRadius: theme.shape.borderRadius,
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
                minHeight: 400,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    marginBottom: theme.spacing(2),
                    fontSize: '1rem',
                  }}
                >
                  Background Check Status
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.backgroundCheckStatus}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                    <XAxis
                      dataKey="status"
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: '0.75rem' }}
                    />
                    <YAxis
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: '0.75rem' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.customColors.tableBorder}`,
                        borderRadius: theme.shape.borderRadius,
                      }}
                      labelStyle={{ color: theme.palette.text.primary }}
                    />
                    <Bar dataKey="count" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Renewal Reminders Table */}
        <Card
          sx={{
            border: `1px solid ${theme.customColors.tableBorder}`,
            marginTop: theme.spacing(3),
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(2),
                fontSize: '1rem',
              }}
            >
              Renewal Reminders
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>PI NAME</TableHeaderCell>
                  <TableHeaderCell>ITEM</TableHeaderCell>
                  <TableHeaderCell>EXPIRY DATE</TableHeaderCell>
                  <TableHeaderCell>STATUS</TableHeaderCell>
                  <TableHeaderCell>ACTION</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.renewalReminders.map((reminder, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {reminder.piName}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {reminder.item}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {reminder.expiryDate}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={reminder.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(reminder.status),
                          color: theme.palette.common.white,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => console.log('View:', reminder.piName)}
                        sx={{
                          color: theme.palette.primary.main,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        View
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>

      {/* Task Compliance Section */}
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
            fontSize: '1rem',
          }}
        >
          Task Compliance
        </Typography>
        <Grid container spacing={2} sx={{ marginBottom: theme.spacing(3) }}>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                  }}
                >
                  Flagged Tasks
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.warning.main,
                  }}
                >
                  {data.taskCompliance.flaggedTasks}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                  }}
                >
                  Inconsistent Reports
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.warning.main,
                  }}
                >
                  {data.taskCompliance.inconsistentReports}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                border: `1px solid ${theme.customColors.tableBorder}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.75rem',
                    marginBottom: theme.spacing(0.5),
                  }}
                >
                  System Alerts
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.error.main,
                  }}
                >
                  {data.taskCompliance.systemAlerts}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Flagged Task Details Table */}
        <Card
          sx={{
            border: `1px solid ${theme.customColors.tableBorder}`,
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(2),
                fontSize: '1rem',
              }}
            >
              Flagged Task Details
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>TASK ID</TableHeaderCell>
                  <TableHeaderCell>PI NAME</TableHeaderCell>
                  <TableHeaderCell>FLAG REASON</TableHeaderCell>
                  <TableHeaderCell>DATE</TableHeaderCell>
                  <TableHeaderCell>RISK LEVEL</TableHeaderCell>
                  <TableHeaderCell>ACTION</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.flaggedTasks.map((task, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {task.taskId}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {task.piName}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {task.flagReason}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {formatDateTime(task.date)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.riskLevel}
                        size="small"
                        sx={{
                          backgroundColor: getRiskColor(task.riskLevel),
                          color: theme.palette.common.white,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => console.log('Investigate:', task.taskId)}
                        sx={{
                          color: theme.palette.primary.main,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Investigate
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>

      {/* Data Access Logs Section */}
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
            fontSize: '1rem',
          }}
        >
          Data Access Logs - Sensitive Data Access
        </Typography>

        {/* Suspicious Pattern Alert */}
        {data.suspiciousPatternAlert && (
          <Alert
            severity="error"
            icon={<WarningIcon />}
            sx={{
              marginBottom: theme.spacing(3),
              backgroundColor: `${theme.palette.error.main}20`,
              border: `1px solid ${theme.palette.error.main}`,
            }}
            action={
              <PrimaryButton
                color="error"
                size="small"
                onClick={() => console.log('Take action')}
              >
                Take Action
              </PrimaryButton>
            }
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(0.5),
              }}
            >
              {data.suspiciousPatternAlert.message}
            </Typography>
            <Typography variant="body2">
              {data.suspiciousPatternAlert.details}
            </Typography>
          </Alert>
        )}

        {/* Access Pattern Chart */}
        {data.accessPattern && (
          <Card
            sx={{
              border: `1px solid ${theme.customColors.tableBorder}`,
              marginBottom: theme.spacing(3),
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  fontSize: '1rem',
                }}
              >
                Access Pattern
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data.accessPattern}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.customColors.tableBorder}`,
                      borderRadius: theme.shape.borderRadius,
                    }}
                    labelStyle={{ color: theme.palette.text.primary }}
                  />
                  <Line
                    type="monotone"
                    dataKey="accesses"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Accesses"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Data Access Logs Table */}
        <Card
          sx={{
            border: `1px solid ${theme.customColors.tableBorder}`,
          }}
        >
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>USER</TableHeaderCell>
                  <TableHeaderCell>DATA ACCESSED</TableHeaderCell>
                  <TableHeaderCell>TIMESTAMP</TableHeaderCell>
                  <TableHeaderCell>IP ADDRESS</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.dataAccessLogs.map((log, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {log.user}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {log.dataAccessed}
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                      {formatDateTime(log.timestamp)}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: log.riskLevel === 'High' ? theme.palette.error.main : theme.palette.text.primary,
                        fontSize: '0.875rem',
                        fontWeight: log.riskLevel === 'High' ? 600 : 400,
                      }}
                    >
                      {log.ipAddress}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default ComplianceRiskPage;

